import { useNavigate } from "@tanstack/react-router";
import { useEffect, type CSSProperties, type ReactNode } from "react";
import { TERMINAL_NEGATIVOS, type PasoSolicitud } from "@/components/solicitudes/stepper/steps-config";
import { SolicitudStepper } from "./SolicitudStepper";

/**
 * Cáscara de las páginas del wizard · columna flex con altura mínima
 * igual al viewport disponible (menos el header y el padding del shell).
 * Referencia las CSS vars que expone `AppShell.tsx` (Etapa 4) —
 * `--shell-header-h`/`--shell-padding`, MISMOS nombres semánticos que las
 * `--app-shell-*` que Mantine generaba, para no tener que tocar las ~15
 * pantallas que consumen esto cuando se migren (Etapa 6).
 */
export const PASO_PAGE_SHELL: CSSProperties = {
  minHeight: "calc(100dvh - var(--shell-header-h) - 2 * var(--shell-padding))",
  display: "flex",
  flexDirection: "column",
};

export interface LayoutConStepperProps {
  estado: string | null;
  subtipo: string | null;
  tipo?: string | null;
  paso?: PasoSolicitud;
  solicitudId?: string;
  contabilizada?: boolean;
  fillHeight?: boolean;
  esperaCfdiConciliadoAt?: string | null;
  children: ReactNode;
}

/**
 * Layout de página con el stepper como sidebar izquierdo (sticky) y el
 * contenido del paso a la derecha · reemplazo del `Grid`/`Grid.Col` de
 * Mantine por CSS Grid de 12 columnas (mismos breakpoints/spans:
 * `{base:12, md:4, lg:3}` para el stepper, `{base:12, md:8, lg:9}` para
 * el contenido).
 */
export function LayoutConStepper({
  estado,
  subtipo,
  tipo,
  paso,
  solicitudId,
  contabilizada,
  fillHeight = false,
  esperaCfdiConciliadoAt = null,
  children,
}: LayoutConStepperProps) {
  const navigate = useNavigate();
  const esTerminalNegativo = estado !== null && TERMINAL_NEGATIVOS.has(estado);

  useEffect(() => {
    if (esTerminalNegativo && solicitudId) {
      void navigate({ to: "/solicitudes/$id", params: { id: solicitudId }, replace: true });
    }
  }, [esTerminalNegativo, solicitudId, navigate]);

  if (esTerminalNegativo) return null; // el efecto redirige al detalle

  return (
    <div className={["mt-4 grid grid-cols-12 gap-4", fillHeight ? "min-h-0 flex-1" : ""].join(" ")}>
      <div className={["col-span-12 md:col-span-4 lg:col-span-3", fillHeight ? "h-full" : "sticky top-[calc(var(--shell-header-h)+16px)] self-start"].join(" ")}>
        <SolicitudStepper
          estado={estado}
          subtipo={subtipo}
          tipo={tipo}
          paso={paso}
          solicitudId={solicitudId}
          contabilizada={contabilizada}
          fillHeight={fillHeight}
          esperaCfdiConciliadoAt={esperaCfdiConciliadoAt}
        />
      </div>
      <div className="col-span-12 md:col-span-8 lg:col-span-9">{children}</div>
    </div>
  );
}
